import { ReorderCardItem } from '../../../components/reorder-card-item';
import {
    toEventCardData,
    toFeaturedCardData,
    toGameCardData,
    toPromotionCardData,
} from './reorder-helpers';
import type {
    SectionPlacement,
    SectionPlacementItem,
    SectionPlacementFeaturedItem,
    SectionPlacementPromotionItem,
    SectionPlacementEventItem,
    SectionPlacementGameItem,
} from '../../../stores/section-placements';

type PlacementHeaderActions = {
    featured: () => void;
    coupons: () => void;
    inStore: () => void;
    events: () => void;
    games: () => void;
    fallback: () => void;
};

export type PlacementHeaderConfig = {
    title: string;
    viewAllLabel?: string;
    onViewAll?: () => void;
};

export const getPlacementHeader = (
    placement: SectionPlacement,
    actions: PlacementHeaderActions
): PlacementHeaderConfig => {
    switch (placement.key) {
        case 'featured':
            return {
                title: 'Featured banner carousel',
                viewAllLabel: 'View all featured',
                onViewAll: actions.featured,
            };
        case 'featured.mealdeal':
            return {
                title: 'Meal Deal',
            };
        case 'promotions.coupons':
            return {
                title: 'Coupons',
                viewAllLabel: 'View all coupons',
                onViewAll: actions.coupons,
            };
        case 'promotions.in_store':
            return {
                title: 'In-store promotions',
                viewAllLabel: 'View all in-store promotions',
                onViewAll: actions.inStore,
            };
        case 'promotions.age_restricted':
            return {
                title: 'Age-restricted',
            };
        case 'events':
            return {
                title: 'Events',
                viewAllLabel: 'View all events',
                onViewAll: actions.events,
            };
        case 'games_contests':
            return {
                title: 'Games & Contests',
                viewAllLabel: 'View all games & contests',
                onViewAll: actions.games,
            };
        default:
            return {
                title: placement.label,
                viewAllLabel: `View all ${placement.label}`,
                onViewAll: actions.fallback,
            };
    }
};

type PlacementItemActions = {
    onEditFeatured: (contentId: number) => void;
    onEditPromotionCoupon: (contentId: number) => void;
    onEditPromotionInStore: (contentId: number) => void;
    onEditPromotionFallback: () => void;
    onEditEvent: (contentId: number) => void;
    onEditExternalGame: (contentId: number) => void;
    onEditNhlGame: (contentId: number) => void;
    onEditLabattGame: (contentId: number) => void;
};

export const createPlacementItemRenderer = (
    placement: Pick<SectionPlacement, 'id' | 'key'>,
    actions: PlacementItemActions
) => {
    function PlacementItemRenderer(item: SectionPlacementItem, index: number) {
        if (!item.content) return null;

        if (item.contentType === 'featured') {
            const featured = item.content as SectionPlacementFeaturedItem;
            const data = toFeaturedCardData(featured);
            return (
                <ReorderCardItem
                    key={item.id}
                    data={data}
                    index={index}
                    onEdit={() => actions.onEditFeatured(item.contentId)}
                />
            );
        }

        if (item.contentType === 'promotion') {
            const promotion = item.content as SectionPlacementPromotionItem;
            const promotionType = promotion.type;
            const isAgeRestrictedPlacement =
                placement.key === 'promotions.age_restricted';
            const data = toPromotionCardData(promotion, {
                showTypeTag: isAgeRestrictedPlacement,
            });
            const onEdit = () => {
                if (promotionType === 'coupon') {
                    actions.onEditPromotionCoupon(item.contentId);
                } else if (promotionType === 'in_store') {
                    actions.onEditPromotionInStore(item.contentId);
                } else {
                    actions.onEditPromotionFallback();
                }
            };
            return (
                <ReorderCardItem
                    key={item.id}
                    data={data}
                    index={index}
                    onEdit={onEdit}
                />
            );
        }

        if (item.contentType === 'event') {
            const event = item.content as SectionPlacementEventItem;
            const data = toEventCardData(event);
            return (
                <ReorderCardItem
                    key={item.id}
                    data={data}
                    index={index}
                    onEdit={() => actions.onEditEvent(item.contentId)}
                />
            );
        }

        if (
            item.contentType === 'external_game' ||
            item.contentType === 'nhl_game' ||
            item.contentType === 'labatt_game'
        ) {
            const game = item.content as SectionPlacementGameItem;
            const data = toGameCardData(game);
            const onEdit = () => {
                if (item.contentType === 'external_game') {
                    actions.onEditExternalGame(item.contentId);
                } else if (item.contentType === 'nhl_game') {
                    actions.onEditNhlGame(item.contentId);
                } else {
                    actions.onEditLabattGame(item.contentId);
                }
            };
            return (
                <ReorderCardItem
                    key={item.id}
                    data={data}
                    index={index}
                    onEdit={onEdit}
                />
            );
        }

        return null;
    }

    return PlacementItemRenderer;
};
